// TaskDialog.js
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { TimeSelect } from '../../ui/TimeSelect';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { activityAPI, hospitalityAPI, marketingAPI } from '../../../services/api';
import { Pencil, AlertCircle, Calendar, User, Link2, Clock, Sparkles, Trash2, Mail, MessageSquare, CheckCircle2, Upload, Camera, DollarSign, FileText, Gift, Users, Star, Building2, Download, EyeOff } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { associationAPI } from '../../../services/api';
import api from '../../../services/api';
import { CommunicationDialog } from './CommunicationDialog';

export const TaskDialog = ({ open, onOpenChange, accountId, horseId = null, onSuccess, associations = [], users = [], communications = [], contactEntries = [], accounts = [], horses = [], showAccountDropdown = false }) => {
  const [loading, setLoading] = useState(false);
  const [showCommDialog, setShowCommDialog] = useState(false);
  const [filteredAssociations, setFilteredAssociations] = useState([]);
  const [allCrmEntities, setAllCrmEntities] = useState([]); // accounts + contacts combined
  const [formData, setFormData] = useState({
    activity_name: '',
    description: '',
    end_date_time: '',
    task_status: 'not_started',
    is_sensitive: false,
    association_id: '',
    horse_id: '',
    assigned_to_user_id: '',
    linked_communication_ids: [],
    linked_account_id: '', // maps to crm_entity_id (account OR contact)
  });

  // Fetch all CRM entities (accounts + contacts) when dialog opens
  useEffect(() => {
    if (!open) return;
    api.get('/crm-entities').then(r => {
      setAllCrmEntities(r.data || []);
    }).catch(() => {});
  }, [open]);

  // Fetch associations when selected entity changes
  useEffect(() => {
    const fetchAssociationsForAccount = async () => {
      if (showAccountDropdown && formData.linked_account_id) {
        try {
          const response = await associationAPI.list(formData.linked_account_id);
          const associations = response.data || [];
          
          // Enrich associations with entity names
          const enrichedAssociations = await Promise.all(
            associations.map(async (assoc) => {
              // Get the OTHER entity (not the selected account)
              const otherEntityId = assoc.crm_entity_1_id === formData.linked_account_id 
                ? assoc.crm_entity_2_id 
                : assoc.crm_entity_1_id;
              
              try {
                const entityRes = await api.get(`/crm-entities/${otherEntityId}`);
                return {
                  ...assoc,
                  entityName: entityRes.data.system_name
                };
              } catch (error) {
                console.error(`Error fetching entity ${otherEntityId}:`, error);
                return {
                  ...assoc,
                  entityName: 'Unknown Contact'
                };
              }
            })
          );
          
          setFilteredAssociations(enrichedAssociations);
        } catch (error) {
          console.error('Failed to fetch associations:', error);
          setFilteredAssociations([]);
        }
      } else if (!showAccountDropdown) {
        // In Account context, use passed associations
        setFilteredAssociations(associations);
      } else {
        setFilteredAssociations([]);
      }
    };
    fetchAssociationsForAccount();
  }, [formData.linked_account_id, showAccountDropdown, associations]);

  // Initialize horse_id from prop when dialog opens
  useEffect(() => {
    if (open && horseId) {
      setFormData(prev => ({ ...prev, horse_id: horseId }));
    }
  }, [open, horseId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.activity_name) {
      toast.error('Task name is required');
      return;
    }

    setLoading(true);
    try {
      // Handle date properly - keep as date string without time conversion
      const dataToSubmit = {
        ...formData,
        activity_type: 'task',
        // Use selected entity (account or contact) from dropdown, fall back to pre-linked accountId
        crm_entity_id: formData.linked_account_id || accountId || null,
      };
      
      // Only convert to ISO if there's a date, otherwise set to null
      if (formData.end_date_time) {
        // Set to noon UTC to avoid timezone issues
        const dateParts = formData.end_date_time.split('-');
        const dateObj = new Date(Date.UTC(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]), 12, 0, 0));
        dataToSubmit.end_date_time = dateObj.toISOString();
      } else {
        dataToSubmit.end_date_time = null;
      }
      
      await activityAPI.create(dataToSubmit);
      toast.success('Task created');
      onSuccess?.();
      onOpenChange(false);
      setFormData({
        activity_name: '',
        description: '',
        end_date_time: '',
        task_status: 'not_started',
        is_sensitive: false,
        association_id: '',
        horse_id: '',
        assigned_to_user_id: '',
        linked_communication_ids: [],
        linked_account_id: '',
      });
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="task-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New Task</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="task-name">Task Name *</Label>
            <Input
              id="task-name"
              data-testid="task-name-input"
              value={formData.activity_name}
              onChange={(e) => setFormData({ ...formData, activity_name: e.target.value })}
              placeholder="e.g., Follow up on sale inquiry"
            />
          </div>

          {showAccountDropdown ? (
            /* Horse page or standalone: Account/Contact picker + optional Association + optional Horse */
            <div className="space-y-4">
              {/* Combined Account or Contact selector */}
              <div className="space-y-2">
                <Label>Linked Account or Contact (optional)</Label>
                <Select
                  value={formData.linked_account_id || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, linked_account_id: v === 'none' ? '' : v, association_id: '' })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select account or contact..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {/* Accounts group */}
                    {allCrmEntities.filter(e => e.crm_class === 'account').length > 0 && (
                      <div className="px-2 py-1 text-xs text-muted-foreground font-medium border-t">Accounts</div>
                    )}
                    {allCrmEntities.filter(e => e.crm_class === 'account').map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.system_name}</SelectItem>
                    ))}
                    {/* Contacts group */}
                    {allCrmEntities.filter(e => e.crm_class === 'contact').length > 0 && (
                      <div className="px-2 py-1 text-xs text-muted-foreground font-medium border-t">Contacts</div>
                    )}
                    {allCrmEntities.filter(e => e.crm_class === 'contact').map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.system_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Association — only shown once an entity is selected */}
              {formData.linked_account_id && (
                <div className="space-y-2">
                  <Label>Linked Association (optional)</Label>
                  <Select
                    value={formData.association_id || 'none'}
                    onValueChange={(val) => setFormData({ ...formData, association_id: val === 'none' ? '' : val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select association" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {filteredAssociations.map(assoc => (
                        <SelectItem key={assoc.id} value={assoc.id}>
                          {assoc.entityName || 'Unknown Contact'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Horse — hidden when horse is already pre-linked */}
              {!horseId && (
                <div className="space-y-2">
                  <Label>Linked Horse (optional)</Label>
                  <Select
                    value={formData.horse_id || 'none'}
                    onValueChange={(val) => setFormData({ ...formData, horse_id: val === 'none' ? '' : val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select horse" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {horses.map(horse => (
                        <SelectItem key={horse.id} value={horse.id}>
                          {horse.system_name || horse.registered_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          ) : (
            /* Account Context: Show Association + Horse */
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Linked Association (optional)</Label>
                <Select 
                  value={formData.association_id || 'none'} 
                  onValueChange={(val) => setFormData({ ...formData, association_id: val === 'none' ? '' : val })}
                >
                  <SelectTrigger data-testid="association-select">
                    <SelectValue placeholder="Select association" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {filteredAssociations.map(assoc => (
                      <SelectItem key={assoc.id} value={assoc.id}>
                        {assoc.entityName} - {assoc.relationship?.replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Horse dropdown — hidden when horse is already pre-linked (e.g. creating from a horse's page) */}
              {!horseId && (
              <div className="space-y-2">
                <Label>Linked Horse (optional)</Label>
                <Select 
                  value={formData.horse_id || 'none'} 
                  onValueChange={(val) => setFormData({ ...formData, horse_id: val === 'none' ? '' : val })}
                >
                  <SelectTrigger data-testid="linked-horse-select">
                    <SelectValue placeholder="Select horse" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {horses.map(horse => (
                      <SelectItem key={horse.id} value={horse.id}>
                        {horse.system_name || horse.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="task-due">Due Date</Label>
              <Input
                id="task-due"
                data-testid="task-due-input"
                type="date"
                value={formData.end_date_time}
                onChange={(e) => setFormData({ ...formData, end_date_time: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={formData.task_status} 
                onValueChange={(val) => setFormData({ ...formData, task_status: val })}
              >
                <SelectTrigger data-testid="task-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_started">Not Started</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Assigned To</Label>
            <Select 
              value={formData.assigned_to_user_id || 'none'} 
              onValueChange={(val) => setFormData({ ...formData, assigned_to_user_id: val === 'none' ? '' : val })}
            >
              <SelectTrigger data-testid="assigned-to-select">
                <SelectValue placeholder="Select user" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {users.map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Linked Communications (optional)</Label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={() => setShowCommDialog(true)}
              >
                Create New
              </Button>
            </div>
            <div className="border rounded p-3 max-h-32 overflow-y-auto space-y-2">
              {communications.length === 0 ? (
                <p className="text-xs text-muted-foreground">No communications available</p>
              ) : (
                communications.slice(0, 10).map(comm => (
                  <div key={comm.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`comm-${comm.id}`}
                      checked={formData.linked_communication_ids.includes(comm.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, linked_communication_ids: [...formData.linked_communication_ids, comm.id] });
                        } else {
                          setFormData({ ...formData, linked_communication_ids: formData.linked_communication_ids.filter(id => id !== comm.id) });
                        }
                      }}
                      className="rounded"
                    />
                    <Label htmlFor={`comm-${comm.id}`} className="text-sm cursor-pointer flex-1">
                      {comm.activity_name} - {comm.communication_method?.replace('_', ' ')}
                    </Label>
                  </div>
                ))
              )}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="task-notes">Notes</Label>
            <Textarea
              id="task-notes"
              data-testid="task-notes-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add details..."
              rows={3}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="task-sensitive"
              checked={formData.is_sensitive}
              onChange={(e) => setFormData({ ...formData, is_sensitive: e.target.checked })}
              className="rounded"
            />
            <Label htmlFor="task-sensitive" className="text-sm cursor-pointer">
              Mark as sensitive
            </Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="task-submit-btn">
              {loading ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      
      {/* Nested Communication Dialog */}
      {showCommDialog && (
        <CommunicationDialog
          open={showCommDialog}
          onOpenChange={setShowCommDialog}
          accountId={accountId}
          associations={associations}
          users={users}
          contactEntries={[]}
          onSuccess={(newCommId) => {
            setShowCommDialog(false);
            // Refresh will happen in parent
          }}
        />
      )}
    </Dialog>
  );
};

